import type { ValidModuleForAssignmentEntity } from '@/core/infrastructure/schemas/school/assignment/AssignmentSchema'
import { ValidModuleForAssignmentSchema } from '@/core/infrastructure/schemas/school/assignment/AssignmentSchema'
import { EntityValidator } from '@/core/infrastructure/utils/EntityValidator'

/**
 * Classe représentant un module valide pour la matrice d'assignment
 */
export class ValidModuleForAssignment {
  readonly id: number
  readonly title: string
  readonly order: number
  readonly learningCourseId: number
  readonly learningCourseTitle: string
  readonly learningCourseOrder: number

  constructor(validModule: ValidModuleForAssignmentEntity) {
    this.id = validModule.id
    this.title = validModule.title
    this.order = validModule.order
    this.learningCourseId = validModule.learningCourseId
    this.learningCourseTitle = validModule.learningCourseTitle
    this.learningCourseOrder = validModule.learningCourseOrder
  }

  /**
   * Obtient une représentation textuelle complète du module
   */
  public getFullTitle(): string {
    return `${this.learningCourseTitle} - ${this.title}`
  }

  /**
   * Vérifie si le module appartient à un cours spécifique
   */
  public belongsToCourse(courseId: number): boolean {
    return this.learningCourseId === courseId
  }

  /**
   * Compare deux modules par l'ordre du cours puis par l'ordre du module
   */
  public static compareByOrder(a: ValidModuleForAssignment, b: ValidModuleForAssignment): number {
    // D'abord regrouper par learningCourseId
    if (a.learningCourseId !== b.learningCourseId) {
      return a.learningCourseId - b.learningCourseId
    }
    // Puis par ordre du cours (au sein du même cours)
    if (a.learningCourseOrder !== b.learningCourseOrder) {
      return a.learningCourseOrder - b.learningCourseOrder
    }
    // Enfin par ordre du module
    return a.order - b.order
  }

  /**
   * Groupe les modules par cours
   */
  public static groupByCourse(
    modules: ValidModuleForAssignment[]
  ): Map<number, ValidModuleForAssignment[]> {
    const grouped = new Map<number, ValidModuleForAssignment[]>()

    for (const module of modules) {
      const courseModules = grouped.get(module.learningCourseId) || []
      courseModules.push(module)
      grouped.set(module.learningCourseId, courseModules)
    }

    // Trier les modules de chaque cours
    for (const courseModules of grouped.values()) {
      courseModules.sort((a, b) => a.order - b.order)
    }

    return grouped
  }

  /**
   * Méthode de création statique qui valide les données avant instanciation
   */
  public static create(data: unknown): ValidModuleForAssignment {
    return EntityValidator.validateAndCreate(
      ValidModuleForAssignmentSchema,
      data,
      ValidModuleForAssignment,
      'ValidModuleForAssignment'
    )
  }
}
